import React, {forwardRef, memo, useEffect, useRef} from 'react';
import MaterialTable from "material-table";
import TableIcons from "../../utils/TableIcons";
import AddBox from "@material-ui/icons/AddBox";
import {Button, List, ListItem, ListItemText, TextField, Typography} from "@material-ui/core";
import setDate from "../../utils/setDate";
import RefreshIcon from "@material-ui/icons/Refresh";
import getEnvironment from "../../utils/getEnvironment";

import TrafficSourceStateGroupRenderInputs from "./TrafficSourceStateGroupRenderInputs";
import { last } from 'lodash';

const TrafficSourceStateGroupTable = memo((
    {
        data,
        refetch,
        createTrafficSourceStateGroup,
        updateTrafficSourceStateGroup,
        deleteTrafficSourceStateGroup,
        finalValue,
        setFinalValue,
        setModalData,
        setStateList,
        setTrafficSourceIdList
    }
) => {

    const env = getEnvironment();
    const tableRef = useRef();
    useEffect(() => {
        if (tableRef.current && Object.keys(finalValue).length > 0) {
            const {dataManager, state} = tableRef.current;
            const {lastEditingRow} = state;
            const val = sessionStorage.getItem('lastEditingRow');
            const row = val || false;

            if (row) {
                const { data } = dataManager;
                const currentRow = data.filter(item =>  item.id === row);
                if(currentRow[0] && currentRow[0].tableData) {
                    dataManager.changeRowEditing(dataManager.data[currentRow[0].tableData.id], "update");
                    dataManager.lastEditingRow = currentRow[0];
                }

            }
        }
    }, [finalValue]);

    useEffect(() => {
        sessionStorage.removeItem('lastEditingRow');
    }, [])

    /**
     * Lookup logic
     */
    const blockReasonLookup = {};
    data.allBlockReasons.enumValues.forEach(type => {
        blockReasonLookup[type.name] = type.name;
    });
    const triggerCreate = (newData) => {

        createTrafficSourceStateGroup({
            variables: {
                ...newData,
                ...finalValue
            }
        });
    }

    const triggerUpdate = (newData) => {
        const data = {
            ...newData,
        };

        updateTrafficSourceStateGroup({
            variables: {
                ...data,
                ...finalValue
            }
        });
    };

    return (
        <>
            <MaterialTable
                title=""
                tableRef={tableRef}
                icons={{
                    ...TableIcons,
                    Add: forwardRef((props, ref) => {
                        return (
                            <>
                                {env !== 'prod' ? <AddBox {...props} ref={ref}/> : <></>}
                            </>

                        );
                    }),
                }}
                columns={[
                    {title: 'Id', field: "id", hidden: true},
                    {
                        title: "Name", field: "name", editable: 'onAdd',
                        editComponent: props => {
                            const {rowData, value} = props;
                            const newValue = finalValue || value;
                            const {state} = tableRef.current;
                            const {lastEditingRow, showAddRow} = state;
                            setModalData({
                                data: rowData,
                                value: newValue,
                                showAddRow,
                                triggerCreate,
                                triggerUpdate,
                            });
                            return (
                                <TrafficSourceStateGroupRenderInputs
                                    fieldType={"name"}
                                    editProps={props}
                                    data={data}
                                />
                            )
                        }
                    },
                    {
                        title: "States", field: "stateList", editable: 'always',
                        editComponent: props => {
                            const {rowData, value} = props;
                            const newValue = finalValue && finalValue.stateList.length ? finalValue.stateList : value;
                            const {state} = tableRef.current;
                            const {lastEditingRow, showAddRow} = state;
                            return (
                                <TextField
                                    label={"States"}
                                    value={newValue}
                                    onFocus={() => {
                                        setModalData({
                                            data: rowData,
                                            value: newValue,
                                            modalType: 'stateList',
                                            category: "STATE_LIST",
                                            showAddRow,
                                            triggerCreate,
                                            triggerUpdate,
                                        })
                                        sessionStorage.setItem('lastEditingRow', rowData.id);
                                    }
                                    }
                                    onChange={e => {
                                        props.onChange(e.target.value);
                                    }}
                                    error={!value}
                                />
                            )
                        },
                        render: (rowData) => {
                            const {stateList} = rowData;
                            return ( <List dense style={{height: '250px', overflow: 'scroll'}}>
                                    {stateList.split(',').map((item, index) => {
                                        return (
                                            <ListItem key={`${item}_${index}`} divider alignItems={"flex-start"}
                                                      disableGutters>
                                                <ListItemText disableTypography>
                                                    <Typography variant="body2">
                                                        <>{item}</>
                                                    </Typography>
                                                </ListItemText>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )
                        }
                    },
                    {
                        title: "Traffic Source Ids", field: "trafficSourceIdList", editable: 'always',
                        editComponent: props => {
                            const {rowData, value} = props;
                            const newValue = finalValue && finalValue.trafficSourceIdList.length ? finalValue.trafficSourceIdList : value;
                            const {state} = tableRef.current;
                            const {lastEditingRow, showAddRow} = state;
                            return (
                                <TextField
                                    label={"Traffic Source Ids"}
                                    value={newValue}
                                    onFocus={() => {
                                        setModalData({
                                            data: rowData,
                                            value: newValue,
                                            modalType: 'trafficSourceIdList',
                                            category: "TRAFFIC_SOURCE_ID_LIST",
                                            showAddRow,
                                            triggerCreate,
                                            triggerUpdate,
                                            lastEditingRow
                                        })
                                        sessionStorage.setItem('lastEditingRow', rowData.id);
                                        }
                                    }
                                    onChange={e => {
                                        props.onChange(e.target.value);
                                    }}
                                    error={!value}
                                />
                            )
                        },
                        render: (rowData) => {
                            const {trafficSourceIdList} = rowData;
                           return ( <List dense style={{height: '250px', overflow: 'scroll'}}>
                                {trafficSourceIdList.split(',').map((item, index) => {
                                    return (
                                        <ListItem key={`${item}_${index}`} divider alignItems={"flex-start"}
                                                  disableGutters>
                                            <ListItemText disableTypography>
                                                <Typography variant="body2">
                                                        <>{item}</>
                                                </Typography>
                                            </ListItemText>
                                        </ListItem>
                                    );
                                })}
                            </List>
                           )
                        }
                    },
                    {
                        title: "Block Reason", field: "blockReason", lookup: blockReasonLookup, editable: 'onAdd',
                        editComponent: props => (
                            <TrafficSourceStateGroupRenderInputs
                                fieldType={"blockReason"}
                                editProps={props}
                                data={data}
                                defaultCategory={data.blockReason}
                            />
                        )
                    },
                    {title: "Date Created", field: "dateCreated", editable: "never", filtering: false},
                    {title: "Date Modified", field: "dateModified", editable: "never", filtering: false},
                ]}
                data={data.allTrafficSourceStateGroup.nodes.map(trafficSourceStateGroup => {

                    return {
                        id: trafficSourceStateGroup.id,
                        blockReason: trafficSourceStateGroup.blockReason,
                        name: trafficSourceStateGroup.name,
                        stateList: trafficSourceStateGroup.stateList,
                        trafficSourceIdList: trafficSourceStateGroup.trafficSourceIdList,
                        dateCreated: setDate(trafficSourceStateGroup.dateCreated),
                        dateModified: setDate(trafficSourceStateGroup.dateModified),
                    }
                })}
                editable={{
                    onRowAdd: newData =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                const {name, blockReason} = newData;
                                if (!name || !finalValue.stateList.length || !blockReason || !finalValue.trafficSourceIdList.length)  {
                                    alert('Please fill out all required fields');
                                    reject();
                                } else {
                                    triggerCreate(newData);
                                    resolve();
                                }
                                sessionStorage.removeItem('lastEditingRow');
                            }, 1000);
                        }),
                    onRowUpdate: env === 'test' ? (newData, oldData) =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                const {name, blockReason, trafficSourceIdList, stateList} = newData
                                if (!finalValue.stateList.length && !finalValue.trafficSourceIdList.length && !name && !blockReason) {
                                    alert('Please fill out all required fields');
                                    reject();
                                } else {
                                    triggerUpdate(newData);
                                    resolve();
                                }
                                sessionStorage.removeItem('lastEditingRow');
                            }, 1000);
                        }) : null,
                    onRowDelete: env === 'test' ? oldData =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                deleteTrafficSourceStateGroup({
                                    variables: {
                                        id: oldData.id
                                    }
                                });
                                resolve();
                            }, 1000)
                        }) : null,
                    onRowAddCancelled: () => {
                        setTrafficSourceIdList('')
                        setStateList('')
                        sessionStorage.removeItem('lastEditingRow');
                    },
                    onRowUpdateCancelled: () => {
                        setTrafficSourceIdList('')
                        setStateList('')
                        sessionStorage.removeItem('lastEditingRow');
                    },
                }}
                actions={[
                    {
                        icon: () => <RefreshIcon/>,
                        tooltip: 'Refetch Data',
                        isFreeAction: true,
                        onClick: () => refetch()
                    },
                ]}
                options={{
                    addRowPosition: 'first',
                    filtering: true,
                    pageSize: 300,
                    pageSizeOptions: [300, 600, 800],
                    headerStyle: {
                        minWidth: 'auto',
                    },
                    cellStyle: {
                        minWidth: 350
                    }
                }}
            />
        </>
    );
});

export default TrafficSourceStateGroupTable;
